import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  MapPin, 
  Calendar, 
  Mail, 
  MessageSquare, 
  Globe, 
  Sparkles, 
  Clock, 
  Users,
  ExternalLink,
  Phone,
  CreditCard,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ActivityForHomepage, Business } from '@/types/homepage';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useHomePage } from './HomePageContext';
import { toast } from 'sonner';
import { axiosInstance } from '@/lib/axios';
import { getSessionId } from '@/lib/sessionId';
import { useFeatures } from '@/contexts/FeaturesContext';
import { optimizedImage } from '@/lib/cloudinaryImage';

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-label="WhatsApp"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
  </svg>
);

// Experience type color mapping
const EXPERIENCE_TYPE_COLORS = {
  animal: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-200',
    hover: 'hover:bg-orange-200'
  },
  adventure: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-200',
    hover: 'hover:bg-blue-200'
  },
  cultural: {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border-purple-200',
    hover: 'hover:bg-purple-200'
  },
  nature: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200',
    hover: 'hover:bg-green-200'
  },
  default: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    border: 'border-gray-200',
    hover: 'hover:bg-gray-200'
  }
};

export interface BusinessCardProps {
  business: Business;
  isSelected: boolean;
  onClick?: () => void;
  onSelect?: (business: Business) => void;
  activities?: ActivityForHomepage[];
  source?: 'map' | 'search' | 'list' | 'direct';
}

// Helper function to generate initials or use a placeholder
const getInitials = (name: string): string => {
  const words = name.split(' ');
  if (words.length > 1) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  } else if (name.length > 1) {
    return name.substring(0, 2).toUpperCase();
  }
  return '?';
};

// Helper function to get experience type info
const getExperienceTypeInfo = (hasAnimalActivities: boolean, hasAdventureActivities: boolean) => {
  if (hasAnimalActivities && hasAdventureActivities) {
    return {
      label: 'Animal & Adventure',
      color: 'bg-primary text-white',
      icon: '🦁🏔️'
    };
  } else if (hasAnimalActivities) {
    return {
      label: 'Animal Experience',
      color: 'bg-[#FDA544] text-white',
      icon: '🦁'
    };
  } else if (hasAdventureActivities) {
    return {
      label: 'Adventure Experience',
      color: 'bg-[#4285F4] text-white',
      icon: '🏔️'
    };
  }
  return {
    label: 'Experience',
    color: 'bg-gray-500 text-white',
    icon: '🌟'
  };
};

// Helper function to get booking mode info
const getBookingModeInfo = (bookingMode: string) => {
  switch (bookingMode) {
    case 'platform':
      return {
        label: 'Book Now',
        icon: CreditCard,
        color: 'bg-primary hover:bg-[#1F4220] text-white',
        description: 'Instant booking available'
      };
    case 'redirect':
      return {
        label: 'Visit Website',
        icon: ExternalLink,
        color: 'bg-blue-600 hover:bg-blue-700 text-white',
        description: 'External booking required'
      };
    default:
      return {
        label: 'Contact',
        icon: MessageSquare,
        color: 'bg-gray-600 hover:bg-gray-700 text-white',
        description: 'Contact for booking'
      };
  }
};

// Custom comparison function for React.memo to prevent unnecessary re-renders
function arePropsEqual(prevProps: BusinessCardProps, nextProps: BusinessCardProps) {
  // Check if business object reference or essential properties changed
  if (prevProps.business._id !== nextProps.business._id) {
    return false;
  }
  
  // Check selection state
  if (prevProps.isSelected !== nextProps.isSelected) {
    return false;
  }
  
  // Check activities array length (shallow comparison for performance)
  const prevActivitiesLength = prevProps.activities?.length || 0;
  const nextActivitiesLength = nextProps.activities?.length || 0;
  if (prevActivitiesLength !== nextActivitiesLength) {
    return false;
  }
  
  // All important props are the same
  return true;
}

export const BusinessCard = memo(function BusinessCard({ business, isSelected, onClick, onSelect, activities = [], source = 'list' }: BusinessCardProps) {
  const navigate = useNavigate();
  const features = useFeatures();
  const [isHovered, setIsHovered] = useState(false);
  const [showActivities, setShowActivities] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Homepage context state for custom adventures
  const { 
    businessesWithCustomAdventures,
    checkedBusinesses,
    isLoadingCustomAdventures,
    checkForCustomAdventures
  } = useHomePage();

  // Local loading states
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  
  // Check if this business is currently being checked for custom adventures
  const isCheckingThisBusiness = useMemo(() => 
    isLoadingCustomAdventures && !checkedBusinesses.has(business._id)
  , [isLoadingCustomAdventures, checkedBusinesses, business._id]);

  // Check if this business has custom adventures available
  const hasCustomAdventures = useMemo(() => {
    return businessesWithCustomAdventures.has(business._id);
  }, [businessesWithCustomAdventures, business._id]);

  // Show the custom adventure button if business has them OR if we're currently checking
  const showCustomAdventureButton = hasCustomAdventures || isCheckingThisBusiness;

  // Check for custom adventures on mount if not already checked
  useEffect(() => {
    if (!business._id || checkedBusinesses.has(business._id) || isCheckingThisBusiness) {
      return;
    }

    // Debounced check for custom adventures
    const timeoutId = setTimeout(() => {
      checkForCustomAdventures(business._id).catch(error => {
        console.error(`[BusinessCard][${business.name}] Error checking custom adventure status:`, error);
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [business._id, business.name, checkForCustomAdventures, checkedBusinesses, isCheckingThisBusiness]);

  // Motion variants for consistent animations - memoized
  const cardVariants = useMemo(() => ({
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 }
  }), []);

  // Optimize event handlers with useCallback
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(business);
    } else if (onClick) {
      onClick();
    }
  }, [business, onClick, onSelect, isSelected]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  // Get booking related info from the correct location
  const bookingInfo = useMemo(() => ({
    mode: business.contact?.bookingMode || 'platform',
    url: business.contact?.bookingUrl,
    email: business.contact?.email,
    whatsapp: business.contact?.whatsappNumber
  }), [business]);

  const handleBooking = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isBookingLoading) return;

    if (bookingInfo.mode === 'redirect' && bookingInfo.url) {
      // Fire-and-forget — do NOT await, redirect must not wait for analytics
      axiosInstance.post('/leads/track', {
        businessId: business._id,
        eventType: 'booking_click',
        source,
        sessionId: getSessionId(),
      }).catch(() => {});

      // Append ?ref=wildy so partner sees Wildy traffic in their own analytics
      let targetUrl = bookingInfo.url;
      try {
        const url = new URL(bookingInfo.url);
        url.searchParams.set('ref', 'wildy');
        targetUrl = url.toString();
      } catch {
        // bookingUrl is not a valid absolute URL — use it as-is
      }

      try {
        window.open(targetUrl, '_blank');
        toast.success('Opening external booking page...', { duration: 2000 });
      } catch (error) {
        console.error('[BusinessCard][BOOKING] Failed to open external URL:', error);
        toast.error('Failed to open booking page. Please try again.');
      }
    } else if (bookingInfo.mode === 'platform') {
      setIsBookingLoading(true);
      try {
        const loadingToast = toast.loading('Loading activities...', { duration: 3000 });
        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for UX
        navigate(`/business/${business._id}#activities`);
        toast.dismiss(loadingToast);
      } catch (error) {
        console.error('[BusinessCard][BOOKING] Navigation error:', error);
        toast.error('Failed to load business page. Please try again.');
      } finally {
        setIsBookingLoading(false);
      }
    } else {
      handleClick(e);
    }
  }, [bookingInfo, handleClick, activities, navigate, business, isBookingLoading, source]);

  const handleContact = useCallback((method: 'whatsapp' | 'email', e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      if (method === 'whatsapp' && bookingInfo.whatsapp) {
        const cleanNumber = bookingInfo.whatsapp.replace(/\D/g, '');
        window.open(`https://wa.me/${cleanNumber}`, '_blank');
        toast.success('Opening WhatsApp...', { duration: 2000 });
      } else if (method === 'email' && bookingInfo.email) {
        window.open(`mailto:${bookingInfo.email}`, '_blank');
        toast.success('Opening email client...', { duration: 2000 });
      } else {
        toast.error('Contact information not available');
      }
    } catch (error) {
      console.error('[BusinessCard][CONTACT] Error opening contact method:', error);
      toast.error('Failed to open contact method. Please try again.');
    }
  }, [bookingInfo]);

  // Enhanced custom adventure click handler with proper loading states
  const handleCustomAdventureClick = useCallback(async () => {
    if (!business._id) return;

    setIsNavigating(true);
    try {
      navigate(`/custom-adventures/${business._id}`);
    } catch (error) {
      console.error(`[BusinessCard] Custom adventure navigation error for ${business.name}:`, error);
      setIsNavigating(false);
    }
  }, [business._id, business.name, navigate]);

  const handleActivitiesToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowActivities(prev => !prev);
  }, []);

  const handleActivityClick = useCallback((e: React.MouseEvent, activityId: string) => {
    e.stopPropagation();
    navigate(`/activity/${activityId}`);
  }, [navigate]);

  const experienceType = getExperienceTypeInfo(
    business.hasAnimalActivities || false, 
    business.hasAdventureActivities || false
  );
  const bookingInfoObj = getBookingModeInfo(bookingInfo.mode);

  return (
    <TooltipProvider>
      <motion.div
        layout
        transition={{ 
          duration: 0.3,
          ease: "easeInOut"
        }}
        className={cn(
          // Mobile-first responsive card design
          "rounded-lg sm:rounded-xl overflow-hidden border shadow-sm", 
          "transition-all duration-300 cursor-pointer bg-white relative",
          "touch-manipulation", // Optimize for touch
          // Mobile-optimized selection states
          isSelected 
            ? [
                "border-emerald-500 ring-2 sm:ring-4 ring-emerald-500/25", 
                "shadow-lg sm:shadow-xl shadow-emerald-500/20",
                isMobile ? "transform translate-y-[-2px] scale-[1.01]" : "transform translate-y-[-8px] scale-[1.03]",
                "z-20"
              ]
            : [
                "border-slate-200/60 hover:border-slate-300",
                "hover:shadow-md sm:hover:shadow-lg hover:shadow-slate-900/8", 
                "hover:translate-y-[-1px] sm:hover:translate-y-[-3px]",
                "z-0"
              ]
        )}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          isolation: 'isolate'
        }}
      >
        {/* Visual Selection Indicator - Enhanced for mobile */}
        {isSelected && (
          <>
            <div className="absolute top-0 left-0 right-0 h-1 sm:h-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 z-50"></div>
            <div className="absolute -top-1 sm:-top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 sm:border-l-8 border-r-4 sm:border-r-8 border-b-4 sm:border-b-8 border-l-transparent border-r-transparent border-b-emerald-500 z-50"></div>
          </>
        )}
        
        <div className="relative">
          {/* Image - Mobile optimized aspect ratio */}
          <div className={cn(
            "w-full bg-gray-200 overflow-hidden",
            // Slightly less tall on mobile for better content visibility
            "aspect-[16/10] sm:aspect-[16/9] md:aspect-[5/2]"
          )}>
            {business.images?.cover || business.images?.logo || business.images?.gallery?.[0] ? (
              <img
                src={optimizedImage(
                  Array.isArray(business.images?.cover) 
                    ? business.images.cover[0] 
                    : business.images?.cover || business.images?.logo || business.images?.gallery?.[0],
                  800
                )}
                alt={business.name}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              />
            ) : (
              <img
                src="/images/default-business.jpg"
                alt={business.name || 'Business'}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105 opacity-90"
              />
            )}
          </div>

          {/* Emoji in upper corner - Mobile optimized sizing */}
          <div className="absolute top-2 sm:top-3 left-2 sm:left-3">
            <div className={cn(
              "bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md",
              "w-8 h-8 sm:w-12 sm:h-12" // Smaller on mobile
            )}>
              <span className={cn(
                isMobile ? "text-lg" : "text-2xl"
              )}>{business.emoji || '🏞️'}</span>
            </div>
          </div>

          {/* Experience Type Badge - Mobile optimized */}
          <div className="absolute top-2 sm:top-3 right-2 sm:right-3">
            <span className={cn(
              "inline-flex items-center rounded-full text-xs font-medium shadow-md backdrop-blur-sm",
              "px-2 py-0.5 sm:px-2.5 sm:py-1", // More compact on mobile
              experienceType.color
            )}>
              <span className="mr-1 text-xs sm:text-sm">{experienceType.icon}</span>
              <span className={cn(
                isMobile ? "text-xs" : "text-xs"
              )}>{experienceType.label}</span>
            </span>
          </div>

        </div>

        {/* Content - Mobile optimized spacing */}
        <div className={cn(
          "space-y-2 md:space-y-1.5",
          "p-3" // More compact padding on mobile
        )}>
          <div className="flex items-start justify-between">
            <Button
              variant="link"
              className={cn(
                "p-0 h-auto font-bold line-clamp-1 text-gray-900 hover:text-primary transition-colors",
                "text-base sm:text-lg", // Smaller font on mobile
                "min-h-[44px] sm:min-h-auto", // Touch target on mobile
                "touch-manipulation"
              )}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/business/${business._id}`);
              }}
            >
              {business.name}
            </Button>
            <Badge variant={business.type === 'sanctuary' ? 'secondary' : 'outline'} className={cn(
              "ml-2 shrink-0",
              "text-xs", // Consistent small text
              "px-1.5 py-0.5 sm:px-2 sm:py-1" // More compact on mobile
            )}>
              {business.type?.replace('_', ' ')}
            </Badge>
          </div>

          {/* Location - Mobile optimized */}
          <div className="flex items-center text-gray-500 text-sm">
            <MapPin className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 shrink-0 text-primary" />
            <span className="truncate text-xs sm:text-sm">
              {business.location?.address?.city || 'Location'}, {business.location?.address?.country || 'Country'}
            </span>
          </div>

          {/* Description - Mobile optimized */}
          <p className={cn(
            "text-gray-600 line-clamp-2",
            "text-xs sm:text-sm" // Smaller on mobile
          )}>
            {business.description || 'Experience the wildlife adventure of a lifetime with our expert guides.'}
          </p>

          {/* Activities Preview — only shown when platform booking is enabled */}
          {features.bookingPlatform && activities && activities.length > 0 && (
            <div className="space-y-2">
              <Button 
                variant="ghost" 
                size="sm"
                className={cn(
                  "w-full justify-between text-primary hover:bg-primary/10",
                  "min-h-[44px]", // Mobile touch target
                  "touch-manipulation",
                  "text-xs sm:text-sm" // Smaller text on mobile
                )}
                onClick={handleActivitiesToggle}
              >
                <span>{showActivities ? 'Hide' : 'View'} {activities.length} Activities</span>
                <motion.div
                  animate={{ rotate: showActivities ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                </motion.div>
              </Button>
              
              {showActivities && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-2 pt-2 border-t border-gray-100"
                >
                  {activities.slice(0, 3).map((activity) => (
                    <div 
                      key={activity._id}
                      className={cn(
                        "p-2 bg-gray-50 rounded-md hover:bg-gray-100 cursor-pointer transition-colors duration-200",
                        "min-h-[44px] flex flex-col justify-center", // Mobile touch target
                        "touch-manipulation"
                      )}
                      onClick={(e) => handleActivityClick(e, activity._id)}
                      role="button"
                      tabIndex={0}
                      aria-label={`View ${activity.name} activity details`}
                    >
                      <div className="font-medium text-gray-900 line-clamp-1 text-xs sm:text-sm">{activity.name}</div>
                      <div className="text-gray-600 line-clamp-1 text-xs">{activity.description}</div>
                    </div>
                  ))}
                  {activities.length > 3 && (
                    <div className="text-xs text-gray-500 text-center">
                      +{activities.length - 3} more activities
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          )}

          {/* Action Buttons - Mobile optimized */}
          <div className={cn(
            "flex flex-col gap-2 pt-2",
            "space-y-1.5 sm:space-y-2" // Tighter spacing on mobile
          )}>
            {/* Primary Action Row */}
            <div className="flex gap-2">
              {/* Main action button: redirect always shows, platform only when flag is on */}
              {(bookingInfo.mode === 'redirect' || features.bookingPlatform) ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleBooking}
                      disabled={isBookingLoading}
                      className={cn(
                        "flex-1 font-medium transition-all duration-200",
                        "h-10 sm:h-9",
                        "text-xs sm:text-sm",
                        "min-h-[44px] sm:min-h-auto",
                        "touch-manipulation",
                        bookingInfoObj.color,
                        "transform-gpu hover:scale-[1.02] active:scale-[0.98]",
                        isBookingLoading && "opacity-70 cursor-not-allowed"
                      )}
                    >
                      {isBookingLoading ? (
                        <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent mr-2" />
                      ) : (
                        <bookingInfoObj.icon className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      )}
                      {isBookingLoading ? 'Loading...' : bookingInfoObj.label}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isBookingLoading ? 'Loading booking page...' : bookingInfoObj.description}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                /* Platform booking is off — show a simple View Profile link */
                <Button
                  onClick={handleClick}
                  className="flex-1 font-medium h-10 sm:h-9 text-xs sm:text-sm min-h-[44px] sm:min-h-auto bg-slate-100 hover:bg-slate-200 text-slate-700 border-0"
                >
                  View Profile
                </Button>
              )}

              {/* Contact Buttons - Mobile optimized */}
              <div className="flex gap-1.5 sm:gap-1 shrink-0">
                {bookingInfo.whatsapp && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={(e) => handleContact('whatsapp', e)}
                        variant="outline"
                        size="sm"
                        className={cn(
                          "border-green-500 text-green-600 hover:bg-green-50 hover:border-green-600",
                          "transition-all duration-200 transform-gpu hover:scale-[1.05] active:scale-[0.95]",
                          "h-10 w-10 sm:h-9 sm:w-9 p-0", // Consistent mobile touch target
                          "min-h-[44px] min-w-[44px] sm:min-h-auto sm:min-w-auto", // Mobile touch target
                          "touch-manipulation"
                        )}
                        aria-label={`Contact ${business.name} via WhatsApp`}
                      >
                        <WhatsAppIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="flex items-center gap-2">
                        <WhatsAppIcon className="h-3 w-3" />
                        <span>Contact via WhatsApp</span>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                )}

                {bookingInfo.email && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={(e) => handleContact('email', e)}
                        variant="outline"
                        size="sm"
                        className={cn(
                          "border-blue-500 text-blue-600 hover:bg-blue-50 hover:border-blue-600",
                          "transition-all duration-200 transform-gpu hover:scale-[1.05] active:scale-[0.95]",
                          "h-10 w-10 sm:h-9 sm:w-9 p-0", // Consistent mobile touch target
                          "min-h-[44px] min-w-[44px] sm:min-h-auto sm:min-w-auto", // Mobile touch target
                          "touch-manipulation"
                        )}
                        aria-label={`Contact ${business.name} via email`}
                      >
                        <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3" />
                        <span>Send email</span>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>

            {/* Custom Adventure Button — only shown when customAdventures flag is on */}
            {features.customAdventures && (showCustomAdventureButton || isCheckingThisBusiness) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    disabled={isNavigating}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCustomAdventureClick();
                    }}
                    className={cn(
                      "bg-primary hover:bg-[#224924] text-white border-0 shadow-md hover:shadow-lg",
                      "transition-all duration-200 flex items-center gap-2",
                      "text-xs px-3 py-1.5",
                      "h-8 sm:h-auto min-h-[32px] sm:min-h-[28px]", // Mobile optimized height
                      "min-h-[44px] sm:min-h-auto", // Mobile touch target
                      "touch-manipulation"
                    )}
                  >
                    {isNavigating ? (
                      <>
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        <span>Customize Adventure</span>
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>
                      {isCheckingThisBusiness 
                        ? 'Checking custom adventure availability'
                        : 'Create Your Own Custom Adventure Experience'}
                    </span>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </motion.div>
    </TooltipProvider>
  );
}, arePropsEqual);